import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Shield, ShieldCheck, UserCheck, GraduationCap, ArrowLeft, Crown, TrendingUp, Briefcase, Building2, Landmark } from 'lucide-react';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Administrateur', icon: Crown, color: 'bg-amber-100 text-amber-700 border-amber-300', redirect: '/dashboard' },
  admin: { label: 'Administrateur', icon: Shield, color: 'bg-red-100 text-red-700 border-red-300', redirect: '/dashboard' },
  responsable_pedagogique: { label: 'Resp. Pedagogique', icon: ShieldCheck, color: 'bg-violet-100 text-violet-700 border-violet-300', redirect: '/portail-responsable' },
  administratif: { label: 'Administratif', icon: Briefcase, color: 'bg-sky-100 text-sky-700 border-sky-300', redirect: '/dashboard' },
  formateur: { label: 'Formateur', icon: UserCheck, color: 'bg-blue-100 text-blue-700 border-blue-300', redirect: '/portail-formateur' },
  commercial: { label: 'Commercial', icon: TrendingUp, color: 'bg-teal-100 text-teal-700 border-teal-300', redirect: '/commercial' },
  candidat: { label: 'Candidat', icon: GraduationCap, color: 'bg-emerald-100 text-emerald-700 border-emerald-300', redirect: '/portail-candidat' },
  client: { label: 'Espace Entreprise', icon: Building2, color: 'bg-cyan-100 text-cyan-700 border-cyan-300', redirect: '/portail-client' },
  financeur: { label: 'Espace Financeur', icon: Landmark, color: 'bg-teal-100 text-teal-700 border-teal-300', redirect: '/portail-financeur' },
};

const FONCTION_LABELS = {
  'formateur': 'Formateur',
  'Formateur': 'Formateur',
  'commercial': 'Commercial',
  'Commercial': 'Commercial',
  'administrateur': 'Administrateur',
  'Administrateur': 'Administrateur',
  'referent_pedagogique': 'Ref. Pedagogique',
  'Referent pedagogique': 'Ref. Pedagogique',
  'referent pedagogique': 'Ref. Pedagogique',
  'administratif': 'Administratif',
  'Administratif': 'Administratif',
};

export const LoginPage = ({ espaceForce = null }) => {
  const { login, selectProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const espace = espaceForce || (ROLE_CONFIG[searchParams.get('espace')] ? searchParams.get('espace') : null);
  const next = searchParams.get('next') || (espaceForce ? ROLE_CONFIG[espaceForce].redirect : null);
  const goAfterLogin = (role) => {
    const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.formateur;
    const portails = ['client', 'financeur'];
    navigate(espace && next && (role === espace || (portails.includes(role) && portails.includes(espace))) ? next : cfg.redirect);
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Multi-profile state
  const [profiles, setProfiles] = useState(null);
  const [tempToken, setTempToken] = useState(null);
  const [userName, setUserName] = useState('');
  const [selectingProfile, setSelectingProfile] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.multi_profile) {
        setProfiles(result.profiles);
        setTempToken(result.temp_token);
        setUserName(result.user_name);
      } else {
        goAfterLogin(result?.role || 'formateur');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProfile = async (profile) => {
    setSelectingProfile(true);
    try {
      const userData = await selectProfile(tempToken, profile.id);
      goAfterLogin(userData?.role || profile.role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de selection');
    } finally {
      setSelectingProfile(false);
    }
  };

  const resetToLogin = () => {
    setProfiles(null);
    setTempToken(null);
    setUserName('');
  };

  // Profile selection screen
  if (profiles) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-indigo-300 mb-2">Agrope Consulting</p>
            <h1 className="text-2xl font-bold text-white font-['Plus_Jakarta_Sans']">Bonjour {userName}</h1>
            <p className="text-slate-400 mt-1">Choisissez votre profil de travail</p>
          </div>

          <div className="space-y-3">
            {profiles.map((profile) => {
              const cfg = ROLE_CONFIG[profile.role] || ROLE_CONFIG.formateur;
              const Icon = cfg.icon;
              const fonctionLabel = FONCTION_LABELS[profile.fonction_compte] || profile.fonction_compte || cfg.label;
              return (
                <Card
                  key={profile.id}
                  className="cursor-pointer border-2 border-white/10 bg-white/5 backdrop-blur hover:bg-white/10 hover:border-white/30 transition-all"
                  onClick={() => handleSelectProfile(profile)}
                  data-testid={`profile-${profile.role}`}
                >
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cfg.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold">{fonctionLabel}</p>
                      <p className="text-slate-400 text-sm">{cfg.label}</p>
                    </div>
                    {selectingProfile ? (
                      <Loader2 className="w-5 h-5 text-white/50 animate-spin" />
                    ) : (
                      <div className="text-white/30">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <button
            onClick={resetToLogin}
            className="flex items-center gap-2 text-slate-400 hover:text-white mt-6 mx-auto text-sm transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />Retour a la connexion
          </button>
        </div>
      </div>
    );
  }

  // Login form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4">
      <div className="w-full max-w-md">
      <div className="text-center mb-5" data-testid="login-organisme">
        <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-indigo-300">Agrope Consulting</p>
        <p className="text-xs text-slate-400 mt-1">OF Manager</p>
      </div>
      <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          {espace ? (
            <>
              <div className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center mx-auto mb-3 ${ROLE_CONFIG[espace].color}`} data-testid="login-espace-icon">
                {React.createElement(ROLE_CONFIG[espace].icon, { className: 'w-7 h-7' })}
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="login-espace-titre">{ROLE_CONFIG[espace].label}</CardTitle>
              <p className="text-slate-500 text-sm">Connectez-vous avec l'email et le mot de passe qui vous ont été transmis</p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-xl bg-indigo-600 flex items-center justify-center mx-auto mb-3">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">OF Manager</CardTitle>
              <p className="text-slate-500 text-sm">Connectez-vous a votre espace</p>
            </>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.fr" required
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe" required
                data-testid="login-password"
              />
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Se connecter
            </Button>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default LoginPage;
